/**
 * Product Languages Policy — TreatX Admin
 * =========================================
 *
 * TREATX-PRODUCT-LANGUAGES-ADMIN-01
 *
 * Owner-facing management surface for the runtime PRODUCT-UI language
 * availability. Lists every supported locale (name + native name +
 * code + current availability) and lets a TreatX admin toggle each
 * one on/off. Changes:
 *   - persist server-side in ``platform_config[key=enabled_ui_locales]``
 *   - are auditable via ``platform_config_audit``
 *   - take effect on the next language-selector hydration (frontend
 *     re-hydrates on focus / next visit — no redeploy required).
 *
 * Guardrails (also enforced server-side):
 *   - Cannot disable the default locale (``en``).
 *   - Cannot leave zero locales enabled.
 *   - Unknown codes are rejected.
 *
 * This surface does NOT edit translations. Translation preparation is
 * handled by the Legal Multilingual Editor (independent locale set)
 * and by the standard i18n resource bundles in the repo.
 */

import React, { useEffect, useState } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import { Globe, History, ArrowLeft, Check, Lock } from 'lucide-react';

export default function ProductLanguagesPolicy() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaultLocale, setDefaultLocale] = useState('en');
  const [supported, setSupported] = useState([]);
  const [enabled, setEnabled] = useState(new Set());
  const [history, setHistory] = useState([]);
  const [reason, setReason] = useState('');
  const [meta, setMeta] = useState({ source: null, at: null, by: null });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/product-languages');
      setDefaultLocale(res.data.default_locale || 'en');
      setSupported(res.data.supported_locales || []);
      setEnabled(new Set(res.data.enabled_locales || []));
      setHistory(res.data.history || []);
      setMeta({
        source: res.data.source,
        at: res.data.last_modified_at,
        by: res.data.last_modified_by,
      });
    } catch (err) {
      toast({
        title: 'Failed to load product languages',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleLocale = (code) => {
    if (code === defaultLocale) return; // default is locked
    setEnabled(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const enabledList = supported
    .filter(row => enabled.has(row.code))
    .map(row => row.code);

  const dirty = (() => {
    // Compare current enabled set with the initial server state.
    // We infer initial state from `supported[].enabled` snapshot.
    const initial = new Set(
      supported.filter(row => row.enabled).map(row => row.code)
    );
    if (initial.size !== enabled.size) return true;
    for (const c of enabled) if (!initial.has(c)) return true;
    return false;
  })();

  const handleSave = async () => {
    if (enabledList.length === 0) {
      toast({
        title: 'At least one language must remain enabled',
        variant: 'destructive',
      });
      return;
    }
    if (!enabledList.includes(defaultLocale)) {
      toast({
        title: `The default language (${defaultLocale}) cannot be disabled`,
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      const res = await api.put('/admin/product-languages', {
        locales: enabledList,
        reason: reason || undefined,
      });
      const desc = res?.data?.no_change
        ? 'No change (reaffirmed current selection)'
        : `Enabled: ${(res?.data?.enabled_locales || []).join(', ')}`;
      toast({ title: 'Product languages updated', description: desc });
      setReason('');
      await load();
    } catch (err) {
      toast({
        title: 'Failed to save product languages',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="product-languages-policy">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/treatx-admin')}
              data-testid="product-languages-back"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Globe className="h-6 w-6 text-indigo-600" />
                Product Languages
              </h1>
              <p className="text-sm text-slate-500">
                Controls which supported locales users can select in the product language switcher.
              </p>
            </div>
          </div>
          <Badge className="bg-indigo-500 text-white">Runtime</Badge>
        </div>

        <Card className="p-6">
          <div className="mb-4">
            <h2 className="font-semibold text-slate-900">Supported Locales</h2>
            <p className="text-xs text-slate-500 mt-1">
              Toggle a locale to change its availability in the user-facing language selector.
              The default locale (<code className="text-xs">{defaultLocale}</code>) cannot be disabled.
              This does not affect the Legal Multilingual Editor&apos;s locale set.
            </p>
          </div>

          {loading ? (
            <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>
          ) : (
            <div className="space-y-2" data-testid="product-languages-list">
              {supported.map(row => {
                const isEnabled = enabled.has(row.code);
                const isDefault = row.code === defaultLocale;
                return (
                  <div
                    key={row.code}
                    className={`flex items-center justify-between p-3 rounded-lg border ${isEnabled ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}
                    data-testid={`product-language-row-${row.code}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs uppercase text-slate-500 w-8">{row.code}</span>
                      <div>
                        <div className="text-sm font-medium text-slate-900">
                          {row.native_name || row.name}
                          {isDefault && (
                            <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-slate-500">
                              <Lock className="h-3 w-3" /> default
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">{row.name}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {isEnabled ? (
                        <Badge className="bg-emerald-500 text-white flex items-center gap-1">
                          <Check className="h-3 w-3" /> Enabled
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-500">Disabled</Badge>
                      )}
                      <Button
                        variant={isEnabled ? 'outline' : 'default'}
                        size="sm"
                        disabled={isDefault}
                        onClick={() => toggleLocale(row.code)}
                        data-testid={`product-language-toggle-${row.code}`}
                      >
                        {isDefault ? 'Locked' : (isEnabled ? 'Disable' : 'Enable')}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && (
            <>
              <div className="mt-6">
                <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                  Optional reason for change
                </label>
                <Textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="e.g. Enabling Spanish after QA sign-off on the marketing site"
                  className="mt-1"
                  rows={2}
                  data-testid="product-languages-reason"
                />
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  Source: <code>{meta.source || 'bootstrap_default'}</code>
                  {meta.at && (
                    <>
                      {' '} · last change {formatDateTimeLocale(meta.at)}
                      {meta.by && <> by <code>{meta.by}</code></>}
                    </>
                  )}
                </div>
                <Button
                  onClick={handleSave}
                  disabled={saving || !dirty}
                  data-testid="product-languages-save"
                >
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </>
          )}
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <History className="h-4 w-4 text-slate-500" />
            <h2 className="font-semibold text-slate-900">Recent Changes</h2>
          </div>
          {history.length === 0 ? (
            <div className="text-sm text-slate-500">No changes recorded yet.</div>
          ) : (
            <div className="space-y-2 text-xs" data-testid="product-languages-history">
              {history.map((row, i) => (
                <div key={i} className="p-2 rounded bg-slate-50 border border-slate-200">
                  <div className="flex justify-between">
                    <span className="font-mono text-slate-600">{row.action}</span>
                    <span className="text-slate-500">
                      {row.changed_at ? formatDateTimeLocale(row.changed_at) : ''}
                    </span>
                  </div>
                  <div className="text-slate-500 mt-1">
                    <span>from </span>
                    <code>{Array.isArray(row.old_value) ? row.old_value.join(',') : String(row.old_value)}</code>
                    <span> → </span>
                    <code>{Array.isArray(row.new_value) ? row.new_value.join(',') : String(row.new_value)}</code>
                    {row.actor && <> · by <code>{row.actor}</code></>}
                    {row.actor_role && <> ({row.actor_role})</>}
                  </div>
                  {row.reason && <div className="text-slate-600 italic mt-1">{row.reason}</div>}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
